import { getRoomMinAvgPrice } from '../utils/Generic';
import { getHotelAnalyticsName } from '../utils/Analytics';
import { MULTIPLE_ROOMS } from '../constants';

const productsGA = ({
  hotelName, room, price, roomId,
}) => ([
  {
    category: hotelName,
    name: `${hotelName} | ${room.name}`,
    id: room.code,
    price,
    position: roomId,
    variant: '',
    quantity: '',
  },
]);

const roomDetailEvent = ({
  hotel, room, userCurrency, nights, lang,
}) => {
  const occupancy = room.use_requested;
  const currency = hotel.currencies[0];
  const minAvgPrice = getRoomMinAvgPrice(room, currency.code, nights);
  const hotelName = getHotelAnalyticsName(hotel);
  return {
    event: 'productDetails',
    currencyCode: currency.code,
    languageCode: lang,
    userCurrencyCode: userCurrency.code,
    userPrice: 0,
    numAdults: occupancy.adults,
    numChildren: occupancy.childs,
    numInfants: occupancy.infants,
    hotelName,
    ecommerce: {
      detail: {
        products: productsGA({
          hotelName,
          room,
          roomId: parseInt(room.request_id, 10),
          price: minAvgPrice,
        }),
      },
    },
  };
};

const viewItemListEvent = ({
  rooms,
}) => ({
  event: 'view_item_list',
  ecommerce: {
    items: rooms,
  },
});

const findBestRoom = (hotel, bestValueComb, roomId) => (
  hotel.rooms[roomId].find((room) => room.code === bestValueComb.bestRoomCodes[roomId])
);

const productClickBestRate = (state, bestValueComb) => {
  const { hotel, filterCurrency } = state.booking;
  const { nights } = state.booking.last_requested_info;
  const { lang } = state.i18nState;

  const room1 = findBestRoom(hotel, bestValueComb, MULTIPLE_ROOMS.FIRST);
  const room2 = findBestRoom(hotel, bestValueComb, MULTIPLE_ROOMS.SECOND);
  return [
    addRoomEvent({
      hotel, room: room1, userCurrency: filterCurrency, nights, lang,
    }),
    addRoomEvent({
      hotel, room: room2, userCurrency: filterCurrency, nights, lang,
    }),
  ];
};

const addRoomEvent = ({
  hotel, room, userCurrency, nights, lang,
}) => {
  const currency = hotel.currencies[0];
  const occupancy = room.use_requested;
  const minAvgPrice = getRoomMinAvgPrice(room, currency.code, nights);
  const hotelName = getHotelAnalyticsName(hotel);

  return {
    event: 'productClick',
    currencyCode: currency.code,
    languageCode: lang,
    userCurrencyCode: userCurrency.code,
    userPrice: 0,
    numAdults: occupancy.adults,
    numChildren: occupancy.childs,
    numInfants: occupancy.infants,
    hotelName,
    ecommerce: {
      click: {
        actionField: { list: 'habitaciones' },
        products: productsGA({
          hotelName,
          room,
          roomId: parseInt(room.request_id, 10),
          price: minAvgPrice,
        }),
      },
    },
  };
};

const getChildrenAges = (hotel, paxUse) => {
  const ages = [];
  for (let child = 0; child < paxUse.childs; child++) {
    ages.push(hotel.min_child_age);
  }
  for (let baby = 0; baby < paxUse.infants; baby++) {
    ages.push(hotel.min_child_age - 1);
  }
  return ages.length === 0 ? '' : ages.join(',');
};

const chooseRoomEvent = ({
  room, rateCode, boardCode, lang, utmSource,
  userCurrency, hotel, lastRequestedInfo,
}) => {
  const rate = room.rates.find((r) => r.code === rateCode);
  const board = rate.boards.find((b) => b.code === boardCode);
  const hotelName = getHotelAnalyticsName(hotel);
  const paxUse = room.use_requested;
  return {
    utmSource,
    event: 'roomChoose',
    hotelName,
    nombreHotel: hotelName,
    languageCode: lang,
    tipoHabitacionl: room.name,
    cuartosNoche: lastRequestedInfo.nights,
    fechaDeInicio: lastRequestedInfo.checkin,
    fechaDeSalida: lastRequestedInfo.checkout,
    rateCode,
    boardCode,
    numAdults: paxUse.adults || 2,
    numChildren: (paxUse.childs + paxUse.infants) || 0,
    numRoom: parseInt(room.request_id, 10),
    childrenAges: getChildrenAges(hotel, paxUse),
    userCurrencyCode: userCurrency.code,
    userPrice: board.amount[userCurrency.code].amount,
    ecommerce: {
      currencyCode: hotel.default_currency,
      add: {
        products: [{
          id: room.code,
          category: hotelName,
          name: `${hotelName} | ${room.name}`,
          price: board.amount[hotel.default_currency].amount,
          variant: '',
          quantity: lastRequestedInfo.nights,
        }],
      },
    },
  };
};

const roomListLoadedEvent = ({
  hotel, lastRequestedInfo, userCurrency, lang, utmSource,
}) => {
  // Divisa del hotel para referenciar precio por defecto de las habitaciones.
  const currencyCode = hotel.currencies[0].code;
  const hotelName = getHotelAnalyticsName(hotel);

  let impressions = [];
  Object.keys(hotel.rooms).forEach((roomDictId) => {
    const rooms = hotel.rooms[roomDictId];
    const roomImpressions = rooms.map((room, num) => toRoomImpression({
      hotelName,
      room,
      roomNum: parseInt(roomDictId, 10),
      position: impressions.length + num + 1,
      lastRequestedInfo,
      currencyCode,
    }));
    impressions = impressions.concat(roomImpressions);
  });

  return {
    event: 'productImpressions',
    languageCode: lang,
    userCurrencyCode: userCurrency.code,
    userPrice: 0, // Since there is no cart, in the room list the selection is 0
    hotelName,
    ecommerce: {
      utmSource,
      impressions,
      currencyCode,
    },
  };
};

const toRoomImpression = ({
  hotelName, room, roomNum, position, lastRequestedInfo, currencyCode,
}) => {
  const minAvgPrice = getRoomMinAvgPrice(room, currencyCode, lastRequestedInfo.nights);
  const { distribution } = room;
  return {
    fechaDeInicio: lastRequestedInfo.checkin,
    fechaDeSalida: lastRequestedInfo.checkout,
    numAdults: distribution.adults,
    numChildren: distribution.childs,
    numInfants: distribution.infants,
    numRoom: roomNum,
    category: hotelName,
    name: `${hotelName} | ${room.name}`,
    id: room.code,
    price: minAvgPrice,
    list: 'Habitaciones',
    position,
  };
};

export {
  addRoomEvent, // add the selected room selection to the cart
  productClickBestRate, // adds the combination of two cheapest rooms
  chooseRoomEvent, // select rate or board
  roomDetailEvent,
  viewItemListEvent,
  roomListLoadedEvent,
};
